// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React, { useEffect, useMemo, useState } from 'react';
import {
  type FactFindSchema,
  validateProfileFormSchemaText,
  type SchemaValidationResult,
} from '../../api/profileFormSchemaApi';

interface Props {
  /** Schema to seed the editor with (default or override/inherited). */
  initialSchema: FactFindSchema;
  /** Where the seeded schema came from, for the status line. */
  source: 'GLOBAL' | 'BROKERAGE';
  /** Version of the seeded schema. */
  version: number;
  saving?: boolean;
  /** Server-side save error to surface. */
  error?: string | null;
  onSave: (schema: FactFindSchema) => void;
  /** Present only for overrides — reverts to the global default. */
  onDelete?: () => void;
}

const box: React.CSSProperties = { border: '1px solid #e5e7eb', borderRadius: 6, padding: 16 };
const btn: React.CSSProperties = {
  background: '#1e40af', color: 'white', border: 'none', borderRadius: 4,
  padding: '8px 16px', cursor: 'pointer',
};
const btnGhost: React.CSSProperties = {
  background: 'white', color: '#374151', border: '1px solid #d1d5db', borderRadius: 4,
  padding: '8px 16px', cursor: 'pointer',
};

/**
 * JSON editor for a fact-find schema. Mirrors the intelligence-config
 * "Open as JSON" pattern: a textarea with client-side structural validation
 * before save. The Resource Server re-validates authoritatively on PUT.
 */
const FactFindSchemaEditor: React.FC<Props> = ({
  initialSchema, source, version, saving, error, onSave, onDelete,
}) => {
  const seed = useMemo(() => JSON.stringify(initialSchema, null, 2), [initialSchema]);
  const [text, setText] = useState(seed);
  const [validation, setValidation] = useState<SchemaValidationResult | null>(null);

  // Re-seed when a different schema is loaded (tab switch / brokerage change).
  useEffect(() => {
    setText(seed);
    setValidation(null);
  }, [seed]);

  const onValidate = () => setValidation(validateProfileFormSchemaText(text));

  const onSaveClick = () => {
    const result = validateProfileFormSchemaText(text);
    setValidation(result);
    if (!result.valid) return;
    onSave(JSON.parse(text) as FactFindSchema);
  };

  return (
    <div style={box} data-testid="ff-schema-editor">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 13, color: '#6b7280' }}>
          Source: <strong>{source}</strong> · version <strong>{version}</strong>
          {source === 'GLOBAL' && onDelete && ' (inheriting the global default — saving creates an override)'}
        </span>
        <span style={{ fontSize: 13, color: '#6b7280' }}>
          {validation ? `${validation.questionCount} question(s)` : ''}
        </span>
      </div>

      <textarea
        data-testid="ff-schema-json"
        value={text}
        onChange={(e) => setText(e.target.value)}
        spellCheck={false}
        rows={24}
        style={{
          width: '100%', fontFamily: 'monospace', fontSize: 13,
          border: '1px solid #d1d5db', borderRadius: 4, padding: 8, boxSizing: 'border-box',
        }}
      />

      {validation && (
        <div
          data-testid="ff-schema-validation"
          style={{
            marginTop: 8, padding: 8, borderRadius: 4,
            background: validation.valid ? '#d1fae5' : '#fee2e2',
            color: validation.valid ? '#065f46' : '#991b1b',
          }}
        >
          {validation.valid ? (
            `Valid — ${validation.questionCount} question(s).`
          ) : (
            <>
              <strong>{validation.errors.length} error(s):</strong>
              <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                {validation.errors.map((er, i) => <li key={i}>{er}</li>)}
              </ul>
            </>
          )}
        </div>
      )}

      {error && (
        <div data-testid="ff-schema-error" style={{ marginTop: 8, color: '#b91c1c' }}>{error}</div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          {onDelete && source === 'BROKERAGE' && (
            <button type="button" style={btnGhost} data-testid="ff-schema-revert" onClick={onDelete}>
              Revert to default
            </button>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" style={btnGhost} data-testid="ff-schema-validate" onClick={onValidate}>
            Validate
          </button>
          <button type="button" style={btn} data-testid="ff-schema-save" disabled={saving} onClick={onSaveClick}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FactFindSchemaEditor;
